import React, { useCallback, useMemo, ReactElement } from "react";
import clsx from "clsx";
import { usePersistentToggleState } from "react-migration/components/CheckboxTree/usePersistentToggleState";
import { GenericCategoryFilter } from "react-migration/layouts/map/Constraints/GenericCategoryFilter";
import { SourceStatusFilters } from "react-migration/layouts/map/Constraints/SourceStatusFilters";
import { CATEGORY_ATTRIBUTES } from "react-migration/domains/constraints/designation/style/StyleMap";
import { DesignationHatch } from "react-migration/domains/constraints/components/Hatch";
import { RenderNodeBadge } from "react-migration/components/CheckboxTree/types";
import { ModalV2 } from "react-migration/components/ModalV2/ModalV2";
import { Select } from "react-migration/components/Select/Select";
import {
  getDesignationFillColour,
  getDesignationLineColour,
} from "react-migration/domains/constraints/designation/style/accessors";
import { CheckboxTree, CheckboxTreeChange, TreeStructure } from "react-migration/components/CheckboxTree";
import { LayerTypeControlPageProps } from "../../types";
import { useControlTracking } from "../../ControlTrackingContext";
import { AttributeFilter, ConstraintLayerConfig, isSingleConstraintLayer } from "./types";
import { ConstraintStatus, useLayerStore } from "./ConstraintsContext/store";
import { useConstraintsLayerTypeContext } from "./ConstraintsContext";
import { DISPLAY_BY_CATEGORY } from "./constants";

type TitleWithHelpProps = {
  displayName?: string;
  description?: string | ReactElement;
};

const TitleWithHelp: React.FC<TitleWithHelpProps> = ({ displayName, description }) => (
  <div className="font-semibold flex gap-x-1 items-center">
    <div>{displayName}</div>
    {description && (
      <ModalV2.Root>
        <ModalV2.Trigger>
          <i className="icon-lt-question-line text-lg text-text-base-tertiary" />
        </ModalV2.Trigger>
        <ModalV2.Body title={displayName}>{description}</ModalV2.Body>
      </ModalV2.Root>
    )}
  </div>
);

export function ConstraintsControlPage({ layer, disabled }: LayerTypeControlPageProps) {
  const layerConfig = layer.layerConfig! as ConstraintLayerConfig;
  const { constraintsStore, dispatch } = useConstraintsLayerTypeContext();
  const [layerState, layerActions, layerSelectors] = useLayerStore(layer.id, {
    constraintsStore,
    dispatch,
  });

  const [getCollapsed, setCollapsed] = usePersistentToggleState(
    `constraints-collapsed-categories-${layer.id}-new`,
    true
  );

  const rootCategoryId = isSingleConstraintLayer(layerConfig)
    ? layerConfig.rootCategory
    : layerConfig.rootCategories[0];
  const attributeProperties = CATEGORY_ATTRIBUTES.find((x) => x.id === rootCategoryId);

  const titleComponent = layerConfig.showTitle ? (
    <TitleWithHelp displayName={attributeProperties?.displayName} description={attributeProperties?.description} />
  ) : undefined;

  const renderCategoryNodeBadge = useCallback<RenderNodeBadge>(
    ({ id }) => {
      if (layerConfig.disableHatch) return null;

      return <DesignationHatch id={id} disabled={layerState.displayBy !== DISPLAY_BY_CATEGORY} />;
    },
    [layerConfig.disableHatch, layerState.displayBy]
  );

  if (!layerState) return null;

  return (
    <>
      <div
        className={clsx("flex flex-col space-y-2", {
          "opacity-50": disabled,
        })}
      >
        {layerConfig.displayBy?.showDropdown && (
          <div className="py-1">
            <DisplayByDropdown
              {...layerConfig.displayBy}
              disabled={disabled}
              value={layerState.displayBy}
              onChange={layerActions.setDisplayBy}
            />
          </div>
        )}
        <GenericCategoryFilter
          id={rootCategoryId}
          title={titleComponent}
          disabled={disabled}
          categorySchema={layerConfig.categorySchema}
          visibleCategoryIds={layerState.visibleCategories}
          categoryTree={layerSelectors.getCategoryTree()}
          setVisibleCategories={layerActions.setVisibleCategories}
          renderNodeBadge={renderCategoryNodeBadge}
          getCollapsed={getCollapsed}
          setCollapsed={setCollapsed}
        />
        {layerConfig.attributeFilters?.map((attributeFilter) => (
          <DesignationAttributeFilter
            key={attributeFilter.attribute}
            attributeFilter={attributeFilter}
            setHiddenDesignationAttributes={layerActions.setHiddenDesignationAttributes}
            hiddenDesignationAttributes={layerState.hiddenDesignationAttributes[attributeFilter.attribute]}
            layerConfig={layerConfig}
            disabled={disabled}
            disableHatch={attributeFilter.disableHatch}
            selectedAttribute={layerState.displayBy}
          />
        ))}
      </div>
      {layerConfig.statusSchema && (
        <>
          <div
            className={clsx("pt-2 pb-1 text-base", {
              "opacity-50": disabled,
            })}
          >
            Filters
          </div>
          <div className="py-1">
            <SourceStatusFilters
              hiddenStatuses={layerState.hiddenStatuses as ConstraintStatus[]}
              setHiddenStatuses={layerActions.setHiddenStatuses}
              disabled={disabled}
            />
          </div>
        </>
      )}
    </>
  );
}

type DesignationAttributeFilterProps = {
  attributeFilter: AttributeFilter;
  layerConfig: ConstraintLayerConfig;
  disabled: boolean;
  disableHatch?: boolean;
  hiddenDesignationAttributes: string[] | undefined;
  setHiddenDesignationAttributes(attributeFilter: AttributeFilter, attrIds: string[]): void;
  selectedAttribute?: string;
};

const DesignationAttributeFilter = ({
  attributeFilter,
  layerConfig,
  disabled,
  disableHatch,
  hiddenDesignationAttributes,
  setHiddenDesignationAttributes,
  selectedAttribute,
}: DesignationAttributeFilterProps) => {
  const { trackControlChange } = useControlTracking();
  const firstCategory = layerConfig.categorySchema.flatMap((c) => c.key)[0] || "";
  const { attribute, options } = attributeFilter;
  const attributeProperties = CATEGORY_ATTRIBUTES.find((x) => x.id === attribute);
  const isBeingUsedToStyleLayer = attribute === selectedAttribute;

  const { rootNode, treeSchema } = useMemo(() => {
    const rootNode: TreeStructure = {
      display_name: `${attribute}_filter-root-node`,
      id: `${attribute}_filter-root-node`,
      childNodes: options.map(({ value, display_name }) => ({
        id: value,
        display_name: display_name || value,
        childNodes: [],
      })),
    };

    const treeSchema = rootNode?.childNodes?.map((node) => ({ key: node.id })) || [];

    return { rootNode, treeSchema };
  }, [attribute, options]);

  const titleComponent = (
    <TitleWithHelp displayName={attributeProperties?.displayName} description={attributeProperties?.description} />
  );

  const visibleNodeIds = useMemo(
    () => [
      ...treeSchema.map((x) => x.key).filter((value) => !hiddenDesignationAttributes?.includes(value)),
      rootNode.id,
    ],
    [hiddenDesignationAttributes, rootNode.id, treeSchema]
  );

  const onChangeHandler = useCallback<CheckboxTreeChange>(
    ({ visibleNodeIds, id, checked }) => {
      trackControlChange({ filterName: id, filterState: checked });

      setHiddenDesignationAttributes(
        attributeFilter,
        treeSchema.filter((node) => !visibleNodeIds.includes(node.key)).map((node) => node.key)
      );
    },
    [trackControlChange, setHiddenDesignationAttributes, attributeFilter, treeSchema]
  );

  const renderNodeBadge = useCallback<RenderNodeBadge>(
    ({ id }) => {
      if (disableHatch) return null;

      const fillColor = getDesignationFillColour(
        {
          sub_category_id: firstCategory,
          designation_attributes: [
            {
              id: attribute,
              key: attribute,
              value: id,
            },
          ],
        },
        attribute
      );

      const lineColor = getDesignationLineColour(
        {
          sub_category_id: firstCategory,
          designation_attributes: [
            {
              id: attribute,
              key: attribute,
              value: id,
            },
          ],
        },
        attribute
      );

      return (
        <DesignationHatch
          id={id}
          fillColorOverride={fillColor}
          lineColorOverride={lineColor}
          disabled={!isBeingUsedToStyleLayer}
        />
      );
    },
    [attribute, disableHatch, firstCategory, isBeingUsedToStyleLayer]
  );

  return (
    <div className="">
      <CheckboxTree
        title={titleComponent}
        renderNodeBadge={renderNodeBadge}
        treeStructure={rootNode}
        treeSchema={treeSchema}
        visibleNodeIds={visibleNodeIds}
        onChange={onChangeHandler}
        disabled={disabled}
      />
    </div>
  );
};

type DisplayByDropdownProps = {
  categoryOption?: boolean;
  categoryOptionLabel?: string;
  attributeOptions?: { attribute: string; label: string }[];
  disabled: boolean;
  value: string;
  onChange(displayBy: string): void;
};

function DisplayByDropdown({
  categoryOption,
  categoryOptionLabel,
  attributeOptions,
  disabled,
  value,
  onChange,
}: DisplayByDropdownProps) {
  const { trackControlChange } = useControlTracking();

  const options = useMemo(() => {
    let options = attributeOptions?.slice() || [];

    if (categoryOption) {
      options = [
        {
          label: categoryOptionLabel || "Default",
          attribute: DISPLAY_BY_CATEGORY,
        },
        ...options,
      ];
    }

    return options;
  }, [attributeOptions, categoryOption, categoryOptionLabel]);

  const trackedOnChange = useCallback(
    (displayBy: string) => {
      const label = options.find((option) => option.attribute === displayBy)?.label;
      trackControlChange({ filterName: "display_by", filterState: label });

      onChange(displayBy);
    },
    [trackControlChange, onChange, options]
  );

  return (
    <div className="flex flex-col gap-1">
      <div
        className={clsx("flex flex-row items-center gap-1 text-sm font-medium", {
          "opacity-50": disabled,
        })}
      >
        {/* @todo - htmlFor doesn't work because radix is an overengineered mess */}
        <label>Display by</label>
      </div>
      <Select disabled={disabled} onValueChange={trackedOnChange} value={value}>
        {options.map(({ attribute, label }) => (
          <Select.Option key={attribute} value={attribute}>
            {label}
          </Select.Option>
        ))}
      </Select>
    </div>
  );
}
